import { useState, useEffect, useCallback } from "react";

/**
 * useSelectedOrg - Persists the currently selected organization to localStorage
 *
 * This hook solves the navigation problem where users viewing a child org's settings
 * click to edit a parent org's template, and the "back" navigation should return
 * them to the child org they were viewing, not the parent org that owns the template.
 */

const STORAGE_KEY = "transcriptor_selected_org";
const SYNC_EVENT = "transcriptor_selected_org_sync";

export interface SelectedOrgData {
  orgId: string;
  timestamp: number;
}

/**
 * Get the stored selected org from localStorage
 * Returns null if no org is stored or if the data is older than 1 hour
 */
export function getSelectedOrg(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data: SelectedOrgData = JSON.parse(stored);
    const oneHourMs = 60 * 60 * 1000;

    // Expire after 1 hour to avoid stale context
    if (Date.now() - data.timestamp > oneHourMs) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return data.orgId;
  } catch {
    return null;
  }
}

/**
 * Save the selected org to localStorage and trigger a sync event
 */
export function setSelectedOrg(orgId: string): void {
  const data: SelectedOrgData = {
    orgId,
    timestamp: Date.now(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(SYNC_EVENT));
}

/**
 * Clear the selected org from localStorage and trigger a sync event
 */
export function clearSelectedOrg(): void {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(SYNC_EVENT));
}

/**
 * Hook to get and set the selected organization
 * The setter automatically updates localStorage and triggers a sync event
 */
export function useSelectedOrg(): {
  selectedOrgId: string | null;
  setSelectedOrgId: (orgId: string) => void;
  clearSelectedOrgId: () => void;
} {
  const [selectedOrgId, setSelectedOrgIdState] = useState<string | null>(getSelectedOrg());

  useEffect(() => {
    const handleSync = () => {
      setSelectedOrgIdState(getSelectedOrg());
    };
    window.addEventListener(SYNC_EVENT, handleSync);
    return () => window.removeEventListener(SYNC_EVENT, handleSync);
  }, []);

  return {
    selectedOrgId,
    setSelectedOrgId: setSelectedOrg,
    clearSelectedOrgId: clearSelectedOrg,
  };
}
